import { Link, useParams, Navigate } from "react-router-dom";
import { CalendarDays, Clock, ArrowLeft, ArrowRight } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import CTABanner from "@/components/CTABanner";
import { Button } from "@/components/ui/button";
import { BLOG_POSTS, getBlogPost } from "@/lib/blog-posts";
import SEOHead from "@/components/seo/SEOHead";

const SITE = "https://muthaadvanceddentalcare.in";

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPost(slug) : undefined;

  if (!post) return <Navigate to="/blogs" replace />;

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  // Spread any additional images from the source article evenly through the body
  const bodyImages = post.bodyImages ?? [];
  const imageAt = new Map<number, string>();
  if (bodyImages.length) {
    const headingIdx = post.sections
      .map((s, i) => (s.type === "h2" ? i : -1))
      .filter((i) => i > 0);
    bodyImages.forEach((src, n) => {
      const pos = headingIdx[Math.floor(((n + 1) * headingIdx.length) / (bodyImages.length + 1))];
      if (pos !== undefined && !imageAt.has(pos)) imageAt.set(pos, src);
    });
  }

  return (
    <article>
      <SEOHead
        title={`${post.title} | Mutha's Advanced Dental Care Kalyan`}
        description={post.excerpt}
        canonical={`/blogs/${post.slug}`}
        ogImage={SITE + post.image}
        ogType="article"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: post.title,
            description: post.excerpt,
            image: SITE + post.image,
            datePublished: post.date,
            dateModified: post.date,
            inLanguage: "en-IN",
            author: { "@type": "Person", name: post.author },
            publisher: {
              "@type": "Organization",
              name: "Mutha's Advanced Dental Care",
              logo: { "@type": "ImageObject", url: `${SITE}/logo-mutha.png` },
            },
            mainEntityOfPage: `${SITE}/blogs/${post.slug}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE}/blogs` },
              { "@type": "ListItem", position: 3, name: post.title, item: `${SITE}/blogs/${post.slug}` },
            ],
          },
        ]}
      />
      <section className="bg-secondary py-10 md:py-14">
        <div className="container max-w-4xl">
          <Breadcrumbs items={[{ label: "Blog", path: "/blogs" }, { label: post.title }]} />
          <p className="text-xs font-heading uppercase tracking-[0.2em] text-primary mb-3">{post.categoryLabel}</p>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-secondary-foreground mb-4 leading-tight">
            {post.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-secondary-foreground/70">
            <span>By {post.author}</span>
            <span className="flex items-center gap-1"><CalendarDays className="h-3.5 w-3.5" /> {new Date(post.date).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</span>
            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.readMinutes} min read</span>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="container max-w-4xl">
          <img
            src={post.image}
            alt={post.title}
            className="w-full rounded-xl shadow-soft-lg mb-10"
            loading="eager"
          />

          <div className="prose prose-lg max-w-none font-body text-foreground">
            {post.sections.map((s, i) => {
              const inline = imageAt.get(i);
              const withImage = (node: JSX.Element) =>
                inline ? (
                  <div key={i}>
                    <img
                      src={inline}
                      alt={`${post.title} — illustration`}
                      className="w-full rounded-xl shadow-soft-lg my-8"
                      loading="lazy"
                    />
                    {node}
                  </div>
                ) : (
                  node
                );
              if (s.type === "h2")
                return withImage(<h2 key={i} className="font-heading text-2xl md:text-3xl font-bold mt-10 mb-4 text-secondary-foreground">{s.text}</h2>);
              if (s.type === "h3")
                return <h3 key={i} className="font-heading text-lg md:text-xl font-semibold mt-6 mb-2 text-primary">{s.text}</h3>;
              if (s.type === "ul")
                return (
                  <ul key={i} className="list-disc pl-6 space-y-2 my-4 text-foreground/85">
                    {s.items.map((it, j) => <li key={j}>{it}</li>)}
                  </ul>
                );
              return <p key={i} className="my-4 leading-relaxed text-foreground/85">{s.text}</p>;
            })}
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild>
              <Link to="/book-appointment">Book Appointment</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/blogs"><ArrowLeft className="h-4 w-4 mr-2" /> Back to all blogs</Link>
            </Button>
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-12 bg-muted/30">
          <div className="container max-w-5xl">
            <h2 className="font-heading text-2xl font-bold mb-6">Continue Reading</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {related.map((r) => (
                <Link key={r.slug} to={`/blogs/${r.slug}`} className="group bg-card rounded-xl overflow-hidden border border-border hover:shadow-soft-lg transition">
                  <img src={r.image} alt={r.title} className="h-44 w-full object-cover" loading="lazy" />
                  <div className="p-5">
                    <p className="text-xs font-heading uppercase tracking-[0.18em] text-primary mb-2">{r.categoryLabel}</p>
                    <h3 className="font-heading font-semibold mb-2 group-hover:text-primary transition">{r.title}</h3>
                    <span className="text-sm text-primary inline-flex items-center gap-1">Read more <ArrowRight className="h-3 w-3" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTABanner title="Have Dental Questions?" subtitle="Chat with Dr. Mutha on WhatsApp for free advice" />
    </article>
  );
};

export default BlogPost;